import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Handles all SEO meta tags and canonical URLs
 * 
 * Usage:
 * <SEO 
 *   title="Article Title"
 *   description="Article description"
 *   image="https://example.com/image.jpg"
 *   url="/article/123"
 *   type="article"
 * />
 */

const SEO = ({ 
  title = 'YEGO | Your Trusted News Source',
  description = 'YEGO - Your trusted source for Rwanda news, African culture, and global perspectives.',
  image = '',
  url = '',
  type = 'website',
  article = null,
  noIndex = false,
}) => {
  // Get the base URL from environment or window
  const baseUrl = typeof window !== 'undefined' 
    ? window.location.origin 
    : process.env.REACT_APP_SITE_URL || '';
  
  // Construct full URLs
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const fullImage = image || `${baseUrl}/og-image.png`;
  
  // Construct full title
  const fullTitle = title === 'YEGO | Your Trusted News Source' ? title : `${title} | YEGO`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {/* Canonical URL - Prevents duplicate content */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook / WhatsApp */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="YEGO" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      
      {/* Article specific meta tags */}
      {type === 'article' && article && (
        <>
          <meta property="article:published_time" content={article.published_at || article.created_at} />
          <meta property="article:modified_time" content={article.updated_at || article.created_at} />
          <meta property="article:author" content={article.source || 'YEGO'} />
          {article.category && (
            <meta property="article:section" content={article.category} />
          )}
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Additional SEO tags */}
      <meta name="author" content="YEGO" />
      <meta name="publisher" content="YEGO" />
      <meta name="theme-color" content="#ffffff" />
      
      {/* Structured Data for Google */}
      {type === 'article' && article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": title,
            "description": description,
            "image": fullImage ? [fullImage] : [],
            "datePublished": article.published_at || article.created_at,
            "dateModified": article.updated_at || article.created_at,
            "author": {
              "@type": "Person",
              "name": article.source || "YEGO"
            },
            "publisher": {
              "@type": "Organization",
              "name": "YEGO",
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/favicon.svg`
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": fullUrl
            }
          })}
        </script>
      )}
      
      {/* Website Structured Data */}
      {type === 'website' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "YEGO",
            "url": baseUrl,
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${baseUrl}/search?q={search_term_string}`,
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
